import { queryOptions } from "@tanstack/react-query";
import { License } from "@/types/models";
import api, { ApiCatalog } from "@/api";
import type { NextPageContext } from "next";

/**
 * Both factories accept an optional `ctx` for server-side use, which forwards
 * the request's auth cookies. `ctx` only affects the request, never the query
 * key, so a `prefetchQuery` in `getServerSideProps` and a `useQuery` on the
 * client resolve to the same cache entry and the page hydrates without
 * refetching.
 */
export const licensesQueries = {
  all: () => [ApiCatalog.license.key] as const,
  get: (uniqueId: string = "me", ctx?: NextPageContext) =>
    queryOptions({
      queryKey: [...licensesQueries.all(), uniqueId],
      queryFn: () =>
        api({ ...(ctx && { ctx }) }).get<License[]>(
          ApiCatalog.license.endpoint,
          {
            urlReplacers: [{ id: uniqueId }],
          },
        ),
    }),
  /**
   * Like `get`, but requests every license — including expired ones and
   * not-yet-started (future-dated) renewals that the default endpoint omits.
   * Needed to correctly detect early renewals in the pre-expiry logic.
   *
   * `include_collections` is requested unconditionally rather than exposed as an
   * option, because both consumers of this factory (the pre-expiry banner and
   * the subscriptions page) share its cache entry. An option that did not also
   * widen the query key would let whichever query resolved first decide whether
   * the other one sees `license.collections` at all. It costs one extra batched
   * query on the backend, and the banner simply ignores the field.
   */
  getIncludingInactive: (uniqueId: string = "me", ctx?: NextPageContext) =>
    queryOptions({
      queryKey: [...licensesQueries.all(), uniqueId, "include_all"],
      queryFn: () =>
        api({ ...(ctx && { ctx }) }).get<License[]>(
          ApiCatalog.license.endpoint,
          {
            urlReplacers: [{ id: uniqueId }],
            queryParams: {
              include_all_licenses: true,
              include_collections: true,
            },
          },
        ),
    }),
};
